import Swal from "sweetalert2";
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { 
  ImageIcon, Film, Eye, ShoppingCart, Calendar, Tag, Share2, 
  ChevronRight, ChevronLeft, Play, Info, Check, Plus, Star, Flame, Crown, Shield, Bookmark,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { transformDriveUrl, swapSupabaseProject, handleMediaError, isSupabaseUrl } from "@/lib/imageUtils";
import { ImageLightbox } from "@/components/ImageLightbox";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { cn, cleanAlbumDescription } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Download } from "lucide-react";
import { downloadMedia } from "@/lib/downloadUtils";
import { isDownloadExpired } from "@/lib/downloadRules";
import { ExpiredDownloadDialog } from "@/components/ExpiredDownloadDialog";
import { ZipDownloadDialog } from "@/components/ZipDownloadDialog";
import { RequireProfilePhoto } from "@/components/RequireProfilePhoto";
import { GlobalLoaderTrigger } from "@/contexts/GlobalLoadingContext";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const safeFormatDate = (dateString?: string) => {
  if (!dateString) return "---";
  try {
    // Safari requires 'T' instead of space for ISO dates to parse correctly
    const safeString = dateString.replace(" ", "T");
    const d = new Date(safeString);
    if (isNaN(d.getTime())) return "---";
    return d.toLocaleDateString();
  } catch {
    return "---";
  }
};
interface MediaItem {
  id: string;
  file_url: string;
  thumbnail_url: string | null;
  media_type: string;
  media_subtype: string | null;
}

interface AlbumData {
  id: string;
  name: string;
  description: string | null;
  event_name: string;
  cover_image_url: string | null;
  album_type: string | null;
  meta: any;
  created_at: string;
  media: MediaItem[];
  videos?: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    video_file_url: string | null;
    youtube_url: string;
    price: number;
    media_type: string;
  }[];
}

interface PurchasedMediaItem extends MediaItem {
  buyers: {
    user_id: string;
    full_name: string;
    avatar_url: string | null;
  }[];
}

const SharedAlbum = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState<AlbumData | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [cart, setCart] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<"gallery" | "purchased" | "assigned">("gallery");
  const [purchasedMedia, setPurchasedMedia] = useState<PurchasedMediaItem[]>([]);
  const [loadingPurchased, setLoadingPurchased] = useState(false);
  const [userPurchases, setUserPurchases] = useState<Set<string>>(new Set());
  const [userPurchaseDates, setUserPurchaseDates] = useState<Map<string, string>>(new Map());
  const [userSelections, setUserSelections] = useState<Set<string>>(new Set());
  
  const [expiredDialogState, setExpiredDialogState] = useState<{isOpen: boolean, mediaId: string}>({isOpen: false, mediaId: ""});
  const [savedMediaIds, setSavedMediaIds] = useState<Set<string>>(new Set());
  const { isSubscriber, planId, getPrice, formatPrice } = useSubscription();
  const { isAdmin, user, profile, loading: contextLoading } = useAuth();
  const [unassignedPhotoModal, setUnassignedPhotoModal] = useState<{ open: boolean; mediaId: string | null }>({ open: false, mediaId: null });

  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 200], [0, 1]);
  const [customWatermark, setCustomWatermark] = useState<string | null>(null);
  const [watermarkMode, setWatermarkMode] = useState<'tile' | 'center' | 'fit'>('fit');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.6);
  const [watermarkRotated, setWatermarkRotated] = useState(true);
  const [selectedSector, setSelectedSector] = useState<string>("all");
  const [previewQuality, setPreviewQuality] = useState<string>("low");
  const [vipHighQuality, setVipHighQuality] = useState<boolean>(false);

  const [hasAccess, setHasAccess] = useState(true);
  const [accessPending, setAccessPending] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [isZipDialogOpen, setIsZipDialogOpen] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFreeWedding, setIsFreeWedding] = useState(false);
  const [viewers, setViewers] = useState<any[]>([]);
  const [totalViews, setTotalViews] = useState(0);

  // Embedded Authentication States
  const [authTab, setAuthTab] = useState<"instagram" | "signup" | "login">("instagram");
  const [authLoading, setAuthLoading] = useState(false);
  const [appLogoUrl, setAppLogoUrl] = useState<string>("/app-icon.png");

  // Password Protection States
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [isPasswordUnlocked, setIsPasswordUnlocked] = useState(false);
  const [albumRealPassword, setAlbumRealPassword] = useState("");
  const [albumPasswordInput, setAlbumPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  
  // Instagram Quick Login states
  const [igUser, setIgUser] = useState("");
  const [igPhone, setIgPhone] = useState("");
  const [igTerms, setIgTerms] = useState(true);

  // SignUp states
  const [signUpName, setSignUpName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPhone, setSignUpPhone] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [signUpBirthDate, setSignUpBirthDate] = useState("");
  const [signUpGender, setSignUpGender] = useState("masculino");

  // Login states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const handleInstagramQuickLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!igUser) {
      toast.error("Por favor, insira seu usuário do Instagram.");
      return;
    }
    if (!igTerms) {
      toast.error("Você precisa autorizar os Termos de Entrada Rápida para continuar.");
      return;
    }

    setAuthLoading(true);
    const cleanIg = igUser.trim().replace("@", "").toLowerCase();

    try {
      console.log("[SharedAlbum Auth] Solicitando entrada rápida para Instagram:", `@${cleanIg}`);
      
      const { data, error } = await supabase.functions.invoke("manage-subscription", {
        body: {
          action: "quick_instagram_login",
          instagram: `@${cleanIg}`,
          phone: igPhone.trim()
        }
      });

      if (error || !data?.email) {
        throw new Error(error?.message || "Erro ao processar Entrada Rápida.");
      }

      const { error: authError } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password
      });

      if (authError) throw authError;

      toast.success("Entrada Rápida via Instagram autorizada com sucesso! 📸");
    } catch (err: any) {
      console.error("[SharedAlbum Auth] Erro na Entrada Rápida via Instagram:", err);
      toast.error(err.message || "Erro inesperado ao realizar Entrada Rápida.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = signUpName.trim();
    const cleanEmail = signUpEmail.trim().toLowerCase();
    const cleanPhone = signUpPhone.trim();
    const cleanPassword = signUpPassword;

    if (!cleanName || cleanName.split(" ").length < 2) { 
      toast.error("Por favor, insira seu nome completo."); 
      return; 
    }
    if (!cleanPhone || cleanPhone.length < 10) { 
      toast.error("Insira um número de WhatsApp válido com DDD."); 
      return; 
    }
    if (!signUpBirthDate) { 
      toast.error("Sua data de nascimento é obrigatória."); 
      return; 
    }
    if (!signUpGender) { 
      toast.error("O campo gênero é obrigatório."); 
      return; 
    }
    if (!cleanEmail || !cleanPassword) { 
      toast.error("E-mail e senha são obrigatórios."); 
      return; 
    }
    
    setAuthLoading(true);

    try {
      const { error } = await supabase.auth.signUp({ 
        email: cleanEmail, 
        password: cleanPassword,
        options: {
          data: {
            full_name: cleanName,
            phone: cleanPhone,
            birth_date: signUpBirthDate,
            gender: signUpGender,
            is_player: false
          }
        }
      });
      
      if (error) throw error;
      
      toast.success("Conta criada com sucesso! 📸");
    } catch (error: any) {
      console.error("[SharedAlbum Auth] Erro no cadastro:", error);
      toast.error(error.message || "Erro ao cadastrar. Tente novamente.");
    } finally { 
      setAuthLoading(false); 
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = loginEmail.trim().toLowerCase();
    const cleanPassword = loginPassword;

    if (!cleanEmail || !cleanPassword) {
      toast.error("Preencha todos os campos para entrar.");
      return;
    }

    setAuthLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ 
        email: cleanEmail, 
        password: cleanPassword
      });
      
      if (error) throw error;
      
      toast.success("Bem-vindo de volta!");
    } catch (error: any) {
      console.error("[SharedAlbum Auth] Erro no login:", error);
      toast.error(error.message || "E-mail ou senha inválidos.");
    } finally { 
      setAuthLoading(false); 
    }
  };

  const handleFixConnection = async () => {
    try {
      toast.info("Iniciando Diagnóstico e Limpeza...");
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      window.location.reload();
    } catch (e) {
      window.location.reload();
    }
  };

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        let cleanId = id;
        if (cleanId) {
          cleanId = cleanId.replace(/[^\w-]/g, ''); // Remove qualquer coisa que não seja alfanumérico ou traço (resolve parênteses do WhatsApp)
        }

        if (!cleanId) {
          setFetchError("ID do álbum não foi fornecido.");
          setLoading(false);
          return;
        }
        setLoading(true);
        setFetchError(null);
        
        console.log(`[SharedAlbum] 🟡 Iniciando fetchAlbum para ID: ${cleanId}...`);

        // Proteção contra travamento de conexão: Timeout de 15 segundos para consultas paralelas
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout: A conexão com o servidor de banco de dados demorou muito para responder (15s).")), 15000)
        );

        const fetchPromise = (async () => {
          // Busca álbum, mídia, vídeos vinculados, papel de admin e logo do site em paralelo
          const [albumRes, userRoleRes, mediaRes, videosRes, settingsRes] = await Promise.all([
            supabase
              .from("albums")
              .select("*, events(name)")
              .eq("id", cleanId)
              .single(),
            supabase.from("user_roles").select("user_id").eq("role", "admin").limit(1).maybeSingle(),
            supabase.from("album_media").select("*").eq("album_id", cleanId).range(0, 9999),
            supabase.from("videos").select("*").eq("album_id", cleanId).eq("published", true),
            supabase.from("site_settings").select("value").eq("key", "logo_url").maybeSingle()
          ]);

          return { albumRes, userRoleRes, mediaRes, videosRes, settingsRes };
        })();

        // Race our fetches against the timeout
        const { albumRes, userRoleRes, mediaRes, videosRes, settingsRes } = await Promise.race([
          fetchPromise,
          timeoutPromise
        ]) as any;

        const { data: albumData, error: albumError } = albumRes;
        const { data: role } = userRoleRes;
        const { data: mediaData, error: mediaError } = mediaRes;
        const { data: linkedVideos, error: videosError } = videosRes;
        
        if (settingsRes?.data?.value) {
           setAppLogoUrl(settingsRes.data.value);
        }

        if (albumError || !albumData) {
          console.error("Erro ao buscar álbum:", albumError);
          setFetchError(albumError?.message || "O álbum não foi encontrado no banco de dados.");
          setLoading(false);
          return;
        }

        if (mediaError) {
          console.error("Erro ao buscar mídia:", mediaError);
        }
        if (videosError) {
          console.error("Erro ao buscar vídeos vinculados:", videosError);
        }

        const actualAlbumId = albumData.id;

        let metaData = null;
        if (albumData.description?.includes("---META---")) {
          try {
            metaData = JSON.parse(albumData.description.split("---META---")[1]);
          } catch (e) {}
        }

        const isAlbumPrivate = Boolean(albumData.restricted_access) || albumData.is_public === false;
        const isFreeAlbum = Boolean(metaData?.is_wedding_free);

        // Access Control
        let allowed = true;
        
        // Se o álbum NÃO for gratuito, o login é OBRIGATÓRIO!
        const requireLogin = isAlbumPrivate || !isFreeAlbum;
        
        if (requireLogin && !isAdmin) {
          if (!user) {
            allowed = false;
            setHasAccess(false);
            if (isAlbumPrivate) setShowAccessModal(true);
          } else {
            if (isAlbumPrivate) {
              // Check if tagged as player
              const { data: tagged } = await supabase.from("album_players").select("id").eq("album_id", cleanId).eq("player_id", user?.id).maybeSingle();
              if (tagged) {
                 allowed = true;
                 setHasAccess(true);
              } else {
                 // Check if authorized
                 const { data: permission } = await supabase.from("album_permissions" as any).select("status").eq("album_id", cleanId).eq("user_id", user?.id).maybeSingle();
                 if (permission?.status === 'approved') {
                    allowed = true;
                    setHasAccess(true);
                 } else {
                    allowed = false;
                    setHasAccess(false);
                    if (permission?.status === 'pending') {
                       setAccessPending(true);
                    }
                 }
              }
            } else {
              // É público, mas não gratuito: o usuário logado tem acesso (desde que tenha foto de perfil)
              allowed = true;
              setHasAccess(true);
            }
          }
        } else {
          // isAdmin, ou álbum gratuito para todos
          allowed = true;
          setHasAccess(true);
        }
        
        // Handle password protection
        if (metaData?.password && !isAdmin) {
          setRequiresPassword(true);
          setAlbumRealPassword(metaData.password);
        } else {
          setRequiresPassword(false);
          setIsPasswordUnlocked(true); // If no password required or admin, it's unlocked
        }

        // Fetch custom watermark
        if (metaData?.watermark === false) {
           setCustomWatermark(null);
        } else if (metaData) {
           if (metaData.watermark_url) setCustomWatermark(metaData.watermark_url);
           if (metaData.watermark_mode) setWatermarkMode(metaData.watermark_mode);
           if (metaData.watermark_opacity !== undefined) setWatermarkOpacity(metaData.watermark_opacity);
           if (metaData.watermark_rotated !== undefined) setWatermarkRotated(metaData.watermark_rotated);
              if (metaData.preview_quality) setPreviewQuality(metaData.preview_quality);
              if (metaData.vip_high_quality !== undefined) setVipHighQuality(metaData.vip_high_quality);
        } else {
           let uId = albumData.user_id || (role ? role.user_id : null);
           if (uId) {
               const { data: p } = await supabase.from("profiles").select("watermark_url, watermark_opacity, watermark_rotated").eq("user_id", uId).maybeSingle();
               if (p) {
                 if (p.watermark_url) setCustomWatermark(p.watermark_url);
                 if (p.watermark_opacity !== undefined) setWatermarkOpacity(p.watermark_opacity);
                 if (p.watermark_rotated !== undefined) setWatermarkRotated(p.watermark_rotated);
               }
           }
        }

        // Normaliza vídeos vinculados para o formato MediaItem
        const normalizedVideos = (linkedVideos || []).map(v => ({
          id: v.id,
          file_url: v.video_file_url || v.youtube_url,
          thumbnail_url: v.thumbnail_url,
          media_type: "video",
          media_subtype: "linked_video",
          filename: v.title,
          price: v.price,
          category: (v as any).category || null,
          category_color: (v as any).category_color || null
        }));

        // Combina e ordena: Fotos/Vídeos nativos + Vídeos vinculados
        const combinedMedia = [...(mediaData || []), ...normalizedVideos];

        // ORDENAÇÃO NATURAL: Garante que DSC2 venha antes de DSC10
        const sortedMedia = combinedMedia.sort((a, b) => 
          (a.filename || "").localeCompare(b.filename || "", undefined, { numeric: true, sensitivity: 'base' })
        );
        const finalCoverUrl = transformDriveUrl(albumData.cover_image_url || "", 1200);
        console.log(`🖼️ Carregando Álbum: ${albumData.name} | Capa: ${finalCoverUrl}`);

        let freeWedding = false;
        let isPast3Months = false;
        if (metaData?.is_wedding_free) {
          const createdDate = new Date(albumData.created_at);
          const now = new Date();
          const diffDays = Math.ceil(Math.abs(now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
          if (diffDays <= 90) {
            freeWedding = true;
          } else {
            isPast3Months = true;
          }
        }
        
        if (isPast3Months && metaData) {
          metaData.photo_price = 1.00;
          if (metaData.subscriber_photo_price) metaData.subscriber_photo_price = 1.00;
        }

        setIsFreeWedding(freeWedding);

        setAlbum({
          id: albumData.id,
          name: albumData.name,
          description: cleanAlbumDescription(albumData.description),
          event_name: (albumData.events as any)?.name || "Evento",
          cover_image_url: finalCoverUrl,
          album_type: albumData.album_type || "Geral",
          meta: metaData,
          created_at: albumData.created_at,
          media: sortedMedia.map(m => {
            const fileUrl = m.file_url || "";
            
            // Detecção ultra-robusta de vídeo
            const isVideo = m.media_type?.toLowerCase() === "video" || 
                            m.media_subtype === "linked_video" ||
                            /\.(mp4|mov|webm|avi|mkv)$/i.test(fileUrl) ||
                            fileUrl.includes('youtube.com') ||
                            fileUrl.includes('youtu.be') ||
                            fileUrl.includes('drive.google.com/file/d/');
            
            let thumb = transformDriveUrl(m.thumbnail_url, 400);
            
            // Se for vídeo e não tiver thumbnail, tenta extrair do link ou usa placeholder
            if (!thumb && isVideo) {
              if (fileUrl.includes('youtube.com') || fileUrl.includes('youtu.be') || fileUrl.includes('drive.google.com')) {
                thumb = transformDriveUrl(fileUrl, 400);
              } else {
                // Fallback para vídeos nativos sem thumbnail gerada
                thumb = "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&q=80";
              }
            } else if (!thumb) {
              thumb = transformDriveUrl(fileUrl, 400);
            }
            
            // V7: Se for vídeo, mantemos o link original para o player. Se for foto, aplicamos a CDN.
            const full = isVideo ? fileUrl : transformDriveUrl(fileUrl, 1600);
            
            return {
              ...m,
              thumbnail_url: thumb,
              file_url: full,
              original_file_url: fileUrl,
              media_type: isVideo ? "video" : (m.media_type || "photo")
            };
          })
        });
        setLoading(false);
      } catch (err: any) {
        console.error("Critical error in fetchAlbum:", err);
        const isTimeout = err?.message?.includes("Timeout");
        setFetchError(
          isTimeout 
            ? "A conexão com o servidor de banco de dados demorou muito para responder (15s). Por favor, limpe o cache do seu navegador ou tente novamente abaixo." 
            : (err?.message || "Ocorreu um erro interno ao carregar o álbum.")
        );
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [id, user, isAdmin]);

  // REALTIME UPDATES FOR ALBUM MEDIA
  useEffect(() => {
    if (!id) return;
    const cleanId = id.replace(/[^\w-]/g, '');

    const channel = supabase
      .channel(`album_media_live_${cleanId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "album_media", filter: `album_id=eq.${cleanId}` },
        (payload) => {
          console.log("🔥 Realtime novo arquivo:", payload.new.filename);
          const m = payload.new as MediaItem;
          
          const isVideo = m.media_type === "video" || !!m.filename?.match(/\.(mp4|mov)$/i);
          const fileUrl = m.original_file_url || m.file_url;
          const thumb = isVideo ? m.thumbnail_url : transformDriveUrl(m.thumbnail_url || fileUrl, 600);
          const full = isVideo ? fileUrl : transformDriveUrl(fileUrl, 1600);
          
          const newMedia = {
            ...m,
            thumbnail_url: thumb,
            file_url: full,
            original_file_url: fileUrl,
            media_type: isVideo ? "video" : (m.media_type || "photo")
          };

          setAlbum((prev) => {
            if (!prev) return prev;
            if (prev.media.some(existing => existing.id === newMedia.id)) return prev;
            
            return {
              ...prev,
              media: [newMedia, ...prev.media].sort((a, b) => 
                (a.filename || "").localeCompare(b.filename || "", undefined, { numeric: true, sensitivity: 'base' })
              )
            };
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);


  const trackedRef = useRef(false);
  // Background Tracking Effect (espera o Auth carregar)
  useEffect(() => {
    if (!album || !hasAccess || contextLoading || trackedRef.current) return;
      trackedRef.current = true;
    
    let isMounted = true;
    const cleanId = album.id;
    
    (async () => {
       try {
         await supabase.rpc('track_album_view', { 
           p_album_id: cleanId, 
           p_user_id: profile?.id || null 
         });
       } catch (e) {
         console.warn('View tracking failed', e);
       }
       
       if (profile?.id || user?.id) {
         try {
           const profileId = profile?.id || null;
           
           
           if (profileId) {
             await supabase.from('profiles').update({ last_seen_at: new Date().toISOString() }).eq(profile?.id ? 'id' : 'user_id', profileId);
           }
         } catch (e) {}
       }
       
       if (!isMounted) return;
       
       // Fetch viewers
       try {
         const { data: rawViews } = await supabase
           .from('album_views')
           .select('user_id')
           .eq('album_id', cleanId)
           .order('created_at', { ascending: false })
           .limit(100);
         
         if (!isMounted) return;
         
         if (rawViews && rawViews.length > 0) {
           const uniqueProfileIds = [...new Set(rawViews.map((v) => v.user_id).filter(Boolean))].slice(0, 10);
           if (uniqueProfileIds.length > 0) {
             const { data: profilesData, error: profileErr } = await supabase
               .from('profiles')
               .select('id, user_id, full_name, avatar_url, is_vip')
               .in('id', uniqueProfileIds);
             
             let validProfiles = profilesData || [];
             if (profileErr && profileErr.code === '42703') {
               const { data: fallbackProfiles } = await supabase
                 .from('profiles')
                 .select('id, user_id, full_name, avatar_url')
                 .in('id', uniqueProfileIds);
               validProfiles = fallbackProfiles || [];
             }
             if (isMounted) setViewers(validProfiles);
           }
         }

         const { count } = await supabase.from('album_views').select('*', { count: 'exact', head: true }).eq('album_id', cleanId);
         if (count !== null && isMounted) setTotalViews(count);
       } catch (e) {}
    })();
    
    return () => { isMounted = false; };
  }, [album?.id, hasAccess, contextLoading, profile?.id, user?.id]);

  const handleRequestAccess = async () => {
    if (!user) {
      toast.error("Por favor, faça login para solicitar acesso!");
      navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }

    setRequestLoading(true);
    try {
      const { error } = await supabase
        .from("album_permissions" as any)
        .insert({
          album_id: id?.replace(/[^\w-]/g, ''),
          user_id: user.id,
          status: 'pending'
        });

      if (error) throw error;
      
      setAccessPending(true);
      toast.success("Solicitação enviada com sucesso! Aguarde a aprovação do administrador.");
    } catch (err: any) {
      toast.error("Erro ao solicitar acesso: " + err.message);
    } finally {
      setRequestLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserPurchases = async () => {
      if (!user || !album || !album.media || album.media.length === 0) return;
      try {
        const purchasedIds = new Set<string>();
        const purchaseDates = new Map<string, string>();
        
        // Use the same reliable query approach as PurchasesTab.tsx
        let query = supabase.from('orders').select('id, created_at, status, metadata, order_items(media_id)');
        if (user.email) {
          query = query.or(`user_id.eq.${user.id},metadata->>guest_email.eq.${user.email}`);
        } else {
          query = query.eq('user_id', user.id);
        }
        
        const { data: userOrders, error } = await query.in('status', ['paid', 'completed', 'approved', 'APPROVED', 'approved_anon', 'success', 'succeeded', 'pago', 'concluido', 'finalizado', 'liberado', 'active', 'confirmed']);
        
        if (error) throw error;
        
        if (userOrders) {
          userOrders.forEach((order: any) => {
            const albumMeta = album.description?.includes('---META---')
              ? (() => { try { return JSON.parse(album.description.split('---META---')[1]); } catch(e) { return null; } })()
              : null;
            const isZipOrder = (order.metadata?.album_zip_id === album.id) ||
              (order.metadata?.is_zip && albumMeta?.zip_link && order.metadata?.zip_link === albumMeta?.zip_link) ||
              (order.metadata?.is_zip && (order.description || '').toLowerCase().includes(album.name.toLowerCase()));

            // Zip purchase check
            if (isZipOrder) {
              album.media.forEach(m => {
                purchasedIds.add(m.id);
                if (!purchaseDates.has(m.id)) purchaseDates.set(m.id, order.created_at);
              });
            }
            
            // Add all individual order items
            if (order.order_items && Array.isArray(order.order_items)) {
              order.order_items.forEach((item: any) => {
                if (item.media_id) {
                  purchasedIds.add(item.media_id);
                  if (!purchaseDates.has(item.media_id)) purchaseDates.set(item.media_id, order.created_at);
                }
              });
            }
          });
        }
        
        setUserPurchases(purchasedIds);
        setUserPurchaseDates(purchaseDates);
      } catch (err) {
        console.error('Erro ao buscar compras do usuario:', err);
      }
    };
    fetchUserPurchases();
  }, [user, album?.id]);

  useEffect(() => {
    const fetchUserSelections = async () => {
      if (!user || !album) return;
      try {
        const { data, error } = await supabase
          .from("user_selections" as any)
          .select("media_id")
          .eq("user_id", user.id)
          .eq("album_id", album.id);

        if (!error && data) {
          const ids = new Set(data.map((item: any) => item.media_id));
          setSavedMediaIds(ids);
          setUserSelections(ids);
        }
      } catch (err) {
        console.error("Erro ao buscar seleções do usuário:", err);
      }
    };
    fetchUserSelections();
  }, [user, album?.id]);

  useEffect(() => {
    if (isAdmin && activeTab === "purchased" && album) {
      fetchPurchasedMedia();
    }
  }, [isAdmin, activeTab, album?.id]);

  const fetchPurchasedMedia = async () => {
    if (!album || !album.media || album.media.length === 0) {
      setPurchasedMedia([]);
      setLoadingPurchased(false);
      return;
    }
    setLoadingPurchased(true);
    try {
      const mediaIds = album.media.map(m => m.id);
      const chunkSize = 200;
      let allData: any[] = [];
      
      for (let i = 0; i < mediaIds.length; i += chunkSize) {
        const chunk = mediaIds.slice(i, i + chunkSize);
        const { data, error } = await supabase
          .from("order_items")
          .select(`
            media_id,
            orders!inner (
              status,
              user_id,
              profiles (
                full_name,
                avatar_url
              )
            )
          `)
          .in("orders.status", ["paid", "completed", "approved", "APPROVED", "approved_anon", "success", "succeeded", "pago", "concluido", "finalizado", "liberado", "active", "confirmed"])
          .in("media_id", chunk);

        if (error) throw error;
        if (data) allData = [...allData, ...data];
      }
      
      const data = allData;

      // Group by media_id
      const grouped: Record<string, PurchasedMediaItem['buyers']> = {};
      (data as any[]).forEach(item => {
        const mediaId = item.media_id;
        const buyer = {
          user_id: item.orders.user_id,
          full_name: item.orders.profiles?.full_name || "Usuário",
          avatar_url: item.orders.profiles?.avatar_url
        };
        
        if (!grouped[mediaId]) grouped[mediaId] = [];
        // Prevent duplicate buyers for the same photo if they bought it multiple times (unlikely but possible)
        if (!grouped[mediaId].find(b => b.user_id === buyer.user_id)) {
          grouped[mediaId].push(buyer);
        }
      });

      const items: PurchasedMediaItem[] = album.media
        .filter(m => grouped[m.id])
        .map(m => ({
          ...m,
          buyers: grouped[m.id]
        }));

      setPurchasedMedia(items);
    } catch (err) {
      console.error("Erro ao buscar mídias compradas:", err);
      toast.error("Erro ao carregar dados de vendas");
    } finally {
      setLoadingPurchased(false);
    }
  };

  const parsePriceSafe = (val: any) => {
    if (!val) return 0;
    const num = Number(String(val).replace(',', '.'));
    return isNaN(num) ? 0 : num;
  };

  const calculateTotal = () => {
    let total = 0;
    cart.forEach(id => {
      const m = album?.media.find(item => item.id === id);
      if (m) {
        const isAssignedToMe = Boolean((m as any).assigned_to_user_id && profile && ((m as any).assigned_to_user_id === profile.id || (isSubscriber && (m as any).assigned_to_user_id === "ALL_VIP")));
        let price: number = getPrice(m.media_type, m.media_subtype, isAssignedToMe);
        if (album?.meta && user) {
          if (m.media_type === "photo") {
            if (!isSubscriber && album.meta.photo_price) {
              const p = parsePriceSafe(album.meta.photo_price);
              if (p > 0) price = p;
            }
          } else if (m.media_type === "video") {
            if (!isSubscriber && album.meta.video_price) {
              const p = parsePriceSafe(album.meta.video_price);
              if (p > 0) price = p;
            }
          }
        }
        
        if (profile?.is_noiva && m.media_type === "photo" && album?.created_at) {
            const createdDate = new Date(album.created_at);
            const diffDays = Math.ceil(Math.abs(new Date().getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays > 90) {
                price = 1.00;
            }
        }

        total += price;
      }
    });
    return total;
  };

  const toggleCart = (mediaId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const newCart = new Set(cart);
    if (newCart.has(mediaId)) {
      newCart.delete(mediaId);
    } else {
      if (isSubscriber && album) {
        const m = album.media.find((item: any) => item.id === mediaId);
        if (m) {
          const isAssignedToMe = Boolean(
            (m as any).assigned_to_user_id && 
            profile && 
            ((m as any).assigned_to_user_id === profile.id || (m as any).assigned_to_user_id === "ALL_VIP")
          );
          if (!isAssignedToMe) {
            setUnassignedPhotoModal({ open: true, mediaId });
            return;
          }
        }
      }
      newCart.add(mediaId);
    }
    setCart(newCart);
  };

  const toggleBookmark = async (mediaId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!user) {
      toast.error("Por favor, faça login para salvar fotos em seu perfil.");
      return;
    }
    if (!album) return;

    const isSaved = savedMediaIds.has(mediaId);
    try {
      if (isSaved) {
        const { error } = await supabase
          .from("user_selections" as any)
          .delete()
          .eq("user_id", user.id)
          .eq("media_id", mediaId);

        if (error) throw error;

        setSavedMediaIds(prev => {
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
        setUserSelections(prev => {
          const next = new Set(prev);
          next.delete(mediaId);
          return next;
        });
        toast.success("Foto removida das fotos salvas.");
      } else {
        const { error } = await supabase
          .from("user_selections" as any)
          .insert({
            user_id: user.id,
            album_id: album.id,
            media_id: mediaId
          });

        if (error) throw error;

        setSavedMediaIds(prev => {
          const next = new Set(prev);
          next.add(mediaId);
          return next;
        });
        setUserSelections(prev => {
          const next = new Set(prev);
          next.add(mediaId);
          return next;
        });
        toast.success("Foto salva! Ela aparecerá no seu perfil.");
      }
    } catch (err: any) {
      console.error("Erro ao alternar seleção:", err);
      toast.error("Erro ao salvar a foto.");
    }
  };

  const handleSubmitSelection = async () => {
    if (!user || cart.size === 0) {
      toast.error("Selecione fotos primeiro.");
      return;
    }
    const selectedMedia = Array.from(cart);
    try {
      const clientName = profile?.full_name || user.email;
      const { error } = await supabase.from('orders').insert({
        user_id: user.id,
        status: 'selection',
        type: 'selection',
        total_amount: 0,
        metadata: {
          album_id: album?.id,
          album_name: album?.name,
          selected_media: selectedMedia,
          client_name: clientName,
          client_email: user.email,
          client_phone: profile?.phone || null
        }
      });
      if (error) throw error;

      // Notificar Admin no app e via Push
      try {
        const { data: admins } = await supabase
          .from("profiles")
          .select("id, user_id")
          .eq("email", "hudsonraonefotografia@gmail.com");

        if (admins && admins.length > 0) {
          const notifMsg = `📸 Nova seleção de fotos! ${clientName} selecionou ${selectedMedia.length} fotos no álbum "${album?.name}".`;
          await supabase.from("notifications").insert(
            admins.map((admin: any) => ({
              user_id: admin.user_id,
              message: notifMsg,
              type: "photo_selection",
              link: "/admin?tab=selections",
              metadata: { album_id: album?.id, count: selectedMedia.length }
            }))
          );

          for (const admin of admins) {
            try {
              await supabase.functions.invoke("send-push-notification", {
                body: {
                  target: "user",
                  user_id: admin.user_id,
                  title: "📸 Nova Seleção de Fotos!",
                  message: notifMsg,
                  url: "/admin?tab=selections"
                }
              });
            } catch (pushErr) {}
          }
        }
      } catch (notifErr) {}

      Swal.fire({
        icon: 'success',
        title: 'Seleção Enviada!',
        text: 'Sua seleção foi enviada com sucesso! O fotógrafo já foi notificado.',
        background: '#111',
        color: '#fff',
        confirmButtonColor: '#ff6a00'
      });
      setCart(new Set());
    } catch (err: any) {
      console.error(err);
      toast.error("Erro ao enviar seleção: " + err.message);
    }
  };

  if (loading) {
    return <GlobalLoaderTrigger />;
  }

  if (!user && album && !isFreeWedding) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white overflow-y-auto relative selection:bg-primary selection:text-white pb-12">
         {/* Background Cover Art Cinematic Overlay */}
         <div className="absolute inset-0 z-0 h-screen overflow-hidden">
             <img src={album.cover_image_url || ""} className="w-full h-full object-cover opacity-35 blur-xl scale-110" alt="" />
             <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-[#0a0a0a] to-[#0a0a0a]" />
         </div>

         <div className="relative z-10 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 text-center">
            {/* Top Logo / Brand */}
            <div className="flex flex-col items-center mb-8">
               <img 
                 src={appLogoUrl} 
                 alt="Logo" 
                 className="h-20 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.2)] transition-transform duration-500 hover:scale-105"
               />
            </div>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="bg-[#0f0f0f]/80 backdrop-blur-3xl border border-white/5 p-6 md:p-10 rounded-[2.5rem] max-w-md w-full shadow-[0_30px_60px_rgba(0,0,0,0.8)] text-left relative overflow-hidden"
            >
               {/* Elegant Glow Effect */}
               <div className="absolute -top-32 -right-32 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
               <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

               {/* Album Detail Card Header */}
               <div className="bg-black/40 rounded-[1.5rem] p-5 mb-8 border border-white/5 text-center relative z-10">
                  <p className="text-primary text-[10px] font-black uppercase tracking-[0.3em] mb-2">{album.album_type || "Evento"}</p>
                  <p className="text-xl md:text-2xl font-black text-white mb-3 leading-tight">{album.name}</p>
                  <div className="flex items-center justify-center gap-2 text-white/50 text-xs font-medium">
                     <Calendar className="w-4 h-4" />
                     {safeFormatDate(album.created_at)}
                  </div>
               </div>

               <div className="flex items-center justify-center gap-2 mb-6 relative z-10">
                  <div className="h-[1px] flex-1 bg-gradient-to-r from-transparent to-white/10"></div>
                  <p className="text-[10px] font-black text-primary uppercase tracking-widest text-center">
                     📸 Acesso à Galeria
                  </p>
                  <div className="h-[1px] flex-1 bg-gradient-to-l from-transparent to-white/10"></div>
               </div>

               {/* Custom Premium Tabs */}
               <div className="grid grid-cols-3 bg-white/5 p-1 rounded-xl mb-6 border border-white/5">
                 <button
                   onClick={() => setAuthTab("instagram")}
                   className={cn(
                     "py-2.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all",
                     authTab === "instagram" ? "bg-primary text-black shadow-md" : "text-white/60 hover:text-white"
                   )}
                 >
                   INSTAGRAM 📸
                 </button>
                 <button
                   onClick={() => setAuthTab("signup")}
                   className={cn(
                     "py-2.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all",
                     authTab === "signup" ? "bg-primary text-black shadow-md" : "text-white/60 hover:text-white"
                   )}
                 >
                   CADASTRAR
                 </button>
                 <button
                   onClick={() => setAuthTab("login")}
                   className={cn(
                     "py-2.5 text-[9px] font-black uppercase tracking-widest rounded-lg transition-all",
                     authTab === "login" ? "bg-primary text-black shadow-md" : "text-white/60 hover:text-white"
                   )}
                 >
                   ENTRAR
                 </button>
               </div>

               <AnimatePresence mode="wait">
                 {authTab === "instagram" && (
                   <motion.form
                     key="instagram"
                     initial={{ opacity: 0, x: -10 }}
                     animate={{ opacity: 1, x: 0 }}
                     exit={{ opacity: 0, x: 10 }}
                     onSubmit={handleInstagramQuickLogin}
                     className="space-y-4"
                   >
                     <div className="p-3 bg-primary/10 border border-primary/20 rounded-xl mb-2 text-center">
                       <p className="text-[10px] text-primary leading-relaxed font-bold">
                         📸 <strong>Sem e-mails ou senhas!</strong> Entre instantaneamente informando o seu @ do Instagram e seu WhatsApp. Suas compras e fotos ficam vinculadas a ele.
                       </p>
                     </div>

                     <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Seu Instagram (@)</label>
                       <div className="relative">
                         <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 text-sm font-bold">@</span>
                         <input
                           type="text"
                           required
                           value={igUser}
                           onChange={(e) => setIgUser(e.target.value)}
                           className="w-full h-12 bg-white/5 border border-white/10 rounded-xl pl-9 pr-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                           placeholder="usuario"
                         />
                       </div>
                     </div>

                     <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Seu WhatsApp</label>
                       <input
                         type="tel"
                         required
                         value={igPhone}
                         onChange={(e) => setIgPhone(e.target.value)}
                         className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         placeholder="(00) 99999-9999"
                       />
                     </div>

                     <div className="flex items-start gap-2.5 pt-1">
                       <input
                         type="checkbox"
                         id="igTerms"
                         checked={igTerms}
                         onChange={(e) => setIgTerms(e.target.checked)}
                         className="mt-1 accent-primary"
                       />
                       <label htmlFor="igTerms" className="text-[10px] text-white/40 leading-snug cursor-pointer select-none font-bold">
                         Eu declaro ser o titular deste Instagram e **autorizo a vinculação de minhas fotos** e o acesso rápido a esta plataforma.
                       </label>
                     </div>

                     <Button
                       type="submit"
                       disabled={authLoading}
                       className="w-full h-12 bg-primary hover:bg-primary/95 text-black font-black uppercase text-xs tracking-widest mt-2 rounded-xl shadow-lg shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all"
                     >
                       {authLoading ? "Entrando..." : "Entrada Rápida 📸"}
                     </Button>
                   </motion.form>
                 )}

                 {authTab === "signup" && (
                   <motion.form
                     key="signup"
                     initial={{ opacity: 0, x: -10 }}
                     animate={{ opacity: 1, x: 0 }}
                     exit={{ opacity: 0, x: 10 }}
                     onSubmit={handleSignUp}
                     className="space-y-3"
                   >
                     <div className="space-y-1">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Nome Completo</label>
                       <input
                         type="text"
                         required
                         value={signUpName}
                         onChange={(e) => setSignUpName(e.target.value)}
                         className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         placeholder="Seu nome e sobrenome"
                       />
                     </div>

                     <div className="grid grid-cols-2 gap-3">
                       <div className="space-y-1">
                         <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">E-mail</label>
                         <input
                           type="email"
                           required
                           value={signUpEmail}
                           onChange={(e) => setSignUpEmail(e.target.value)}
                           className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                           placeholder="seu@email.com"
                         />
                       </div>

                       <div className="space-y-1">
                         <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">WhatsApp</label>
                         <input
                           type="tel"
                           required
                           value={signUpPhone}
                           onChange={(e) => setSignUpPhone(e.target.value)}
                           className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                           placeholder="(00) 99999-9999"
                         />
                       </div>
                     </div>

                     <div className="grid grid-cols-2 gap-3">
                       <div className="space-y-1">
                         <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Data de Nascimento</label>
                         <input
                           type="date"
                           required
                           value={signUpBirthDate}
                           onChange={(e) => setSignUpBirthDate(e.target.value)}
                           className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         />
                       </div>

                       <div className="space-y-1">
                         <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Gênero</label>
                         <select
                           value={signUpGender}
                           onChange={(e) => setSignUpGender(e.target.value)}
                           className="w-full h-11 bg-[#151515] border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         >
                           <option value="masculino">Masculino</option>
                           <option value="feminino">Feminino</option>
                           <option value="outro">Outro</option>
                         </select>
                       </div>
                     </div>

                     <div className="space-y-1">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Senha</label>
                       <input
                         type="password"
                         required
                         value={signUpPassword}
                         onChange={(e) => setSignUpPassword(e.target.value)}
                         className="w-full h-11 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         placeholder="Crie sua senha"
                       />
                     </div>

                     <Button
                       type="submit"
                       disabled={authLoading}
                       className="w-full h-12 bg-primary hover:bg-primary/95 text-black font-black uppercase text-xs tracking-widest mt-3 rounded-xl shadow-lg shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all"
                     >
                       {authLoading ? "Processando..." : "Criar Conta 📸"}
                     </Button>
                   </motion.form>
                 )}

                 {authTab === "login" && (
                   <motion.form
                     key="login"
                     initial={{ opacity: 0, x: -10 }}
                     animate={{ opacity: 1, x: 0 }}
                     exit={{ opacity: 0, x: 10 }}
                     onSubmit={handleLogin}
                     className="space-y-4"
                   >
                     <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">E-mail</label>
                       <input
                         type="email"
                         required
                         value={loginEmail}
                         onChange={(e) => setLoginEmail(e.target.value)}
                         className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         placeholder="seu@email.com"
                       />
                     </div>

                     <div className="space-y-1.5">
                       <label className="text-[9px] font-black text-white/50 uppercase tracking-widest">Senha</label>
                       <input
                         type="password"
                         required
                         value={loginPassword}
                         onChange={(e) => setLoginPassword(e.target.value)}
                         className="w-full h-12 bg-white/5 border border-white/10 rounded-xl px-4 text-white text-sm focus:outline-none focus:border-primary transition-all font-bold"
                         placeholder="Sua senha"
                       />
                     </div>

                     <Button
                       type="submit"
                       disabled={authLoading}
                       className="w-full h-12 bg-primary hover:bg-primary/95 text-black font-black uppercase text-xs tracking-widest mt-2 rounded-xl shadow-lg shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all"
                     >
                       {authLoading ? "Entrando..." : "Entrar na Conta 📸"}
                     </Button>
                   </motion.form>
                 )}
               </AnimatePresence>
            </motion.div>
         </div>
      </div>
    );
  }



  if (!album) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-center space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-display font-black text-white  tracking-tighter uppercase">
            ÁLBUM NÃO ENCONTRADO
          </h1>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto uppercase tracking-widest leading-relaxed">
            {fetchError || "Este link pode estar incorreto, o álbum pode ter sido removido ou há um problema de conexão."}
          </p>
        </div>
        
        <div className="flex flex-col gap-3 w-full max-w-xs">
          <Button onClick={() => window.location.reload()} className="bg-primary text-black hover:bg-primary/90 font-black uppercase tracking-widest h-14 rounded-2xl shadow-[0_10px_30px_rgba(255,165,0,0.2)]">
            Tentar Novamente
          </Button>
          <Button onClick={handleFixConnection} variant="outline" className="border-white/10 text-white hover:bg-white/10 font-black uppercase tracking-widest h-14 rounded-2xl">
            Limpar Cache e Reparar Conexão
          </Button>
          <Button onClick={() => navigate("/")} variant="ghost" className="text-xs font-black uppercase tracking-widest text-muted-foreground hover:text-white">
            Voltar ao Início
          </Button>
        </div>
      </div>
    );
  }

  // --- SHOW PASSWORD PROMPT IF NEEDED ---
  if (user && requiresPassword && !isPasswordUnlocked && album) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center p-6 relative">
         <div className="absolute inset-0 z-0 h-screen overflow-hidden">
             <img src={album.cover_image_url || ""} className="w-full h-full object-cover opacity-20 blur-2xl scale-110" alt="" />
             <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/90 via-[#0a0a0a] to-[#0a0a0a]" />
         </div>

         <motion.div 
           initial={{ y: 20, opacity: 0 }}
           animate={{ y: 0, opacity: 1 }}
           className="relative z-10 bg-[#0f0f0f]/80 backdrop-blur-3xl border border-white/5 p-8 rounded-[2.5rem] max-w-sm w-full text-center shadow-[0_30px_60px_rgba(0,0,0,0.8)]"
         >
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/10">
               <Shield className="w-8 h-8 text-primary drop-shadow-[0_0_15px_rgba(255,106,0,0.5)]" />
            </div>
            
            <h2 className="text-2xl font-black text-white mb-2 leading-tight">Álbum Privado</h2>
            <p className="text-xs text-white/50 mb-8 leading-relaxed font-medium">
              Este álbum é protegido. Insira a senha fornecida pelo fotógrafo para acessar as fotos.
            </p>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (albumPasswordInput === albumRealPassword) {
                setIsPasswordUnlocked(true);
                toast.success("Acesso liberado!");
              } else {
                setPasswordError("Senha incorreta. Tente novamente.");
              }
            }} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={albumPasswordInput}
                  onChange={(e) => { setAlbumPasswordInput(e.target.value); setPasswordError(""); }}
                  placeholder="Digite a senha..."
                  className="w-full h-14 bg-white/5 border border-white/10 rounded-2xl px-4 text-center text-white text-base focus:outline-none focus:border-primary transition-all font-bold tracking-widest placeholder:text-white/20"
                />
                {passwordError && <p className="text-red-500 text-[10px] mt-2 font-bold tracking-widest uppercase">{passwordError}</p>}
              </div>

              <Button type="submit" className="w-full h-14 bg-primary hover:bg-primary/90 text-black font-black uppercase text-xs tracking-widest rounded-2xl shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all">
                 Desbloquear
              </Button>
            </form>
         </motion.div>
      </div>
    );
  }

    const allMedia = album?.media || [];
    const isVirtualUser = Boolean(profile && user?.email?.endsWith('.ig@byhudsonraone.com') && isSubscriber);
    const userNeedsPhoto = Boolean(!isFreeWedding && user && (!profile || !profile.avatar_url) && !isAdmin && !isVirtualUser);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-primary selection:text-white font-sans overflow-x-hidden">
      {userNeedsPhoto && <RequireProfilePhoto />}
      {/* Dynamic Header */}
      <motion.header 
        style={{ opacity: headerOpacity }}
        className="fixed top-0 left-0 right-0 z-[100] bg-black/80 backdrop-blur-md border-b border-white/5 h-16 flex items-center px-6 md:px-12 justify-between"
      >
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate("/albums")}
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors border border-white/10"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <img 
            src={appLogoUrl} 
            alt="Logo" 
            className="h-8 w-auto object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              if (target.nextElementSibling) {
                target.nextElementSibling.classList.remove('hidden');
              }
            }}
          />
          <span className="hidden text-primary font-display font-black text-xl tracking-tighter uppercase ">By Hudson Raone</span>
          <div className="h-4 w-px bg-white/20 hidden md:block" />
          <span className="text-sm font-bold truncate max-w-[200px] hidden md:block">
            {(() => {
              const cleaned = cleanAlbumDescription(album.name);
              const base = cleaned || "Galeria";
              return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase();
            })()}
          </span>
        </div>
        {hasAccess && (
          <Button 
            size="sm" 
            className="bg-primary hover:bg-primary/90 text-[10px] font-black uppercase tracking-widest px-6 rounded-full shadow-[0_0_20px_rgba(255,106,0,0.3)]"
            onClick={() => Boolean(album?.meta?.selection_mode) ? (cart.size > 0 ? handleSubmitSelection() : null) : navigate(`/checkout?media_ids=${JSON.stringify(Array.from(cart))}`)}
          >
            {Boolean(album?.meta?.selection_mode) ? (cart.size > 0 ? `ENVIAR SELEÇÃO (${cart.size})` : "SELECIONAR FOTOS") : (cart.size > 0 ? `{Boolean(album?.meta?.selection_mode) ? "ENVIAR SELEÇÃO" : "FINALIZAR SELEÇÃO"} (${cart.size})` : "SELECIONAR FOTOS")}
          </Button>
        )}
      </motion.header>

      {/* Netflix Hero Section */}
      <section className="relative h-[85vh] w-full flex items-end">
        {/* Hero Background */}
        <div className="absolute inset-0 z-0">
          <img 
            src={album.cover_image_url} 
            className={cn("w-full h-full object-cover transition-all duration-700", !hasAccess && "blur-md scale-105 opacity-40")}
            alt={album.name} 
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              if (target.dataset.fallbackTried === "true") return;
              
              const originalUrl = target.src;
              
              // 1. TENTATIVA DE AUTO-CURA (SUPABASE PROJECT SWAP)
              if (originalUrl.includes('supabase.co')) {
                const alternativeUrl = swapSupabaseProject(originalUrl);
                if (alternativeUrl !== originalUrl) {
                  console.warn(`🔄 Auto-Cura Capa: Tentando projeto alternativo`);
                  target.src = alternativeUrl;
                  target.dataset.fallbackTried = "true";
                  return;
                }
              }

              // 2. TENTATIVA DE AUTO-CURA (GOOGLE DRIVE CDN LH3)
              // Tenta extrair ID do drive de qualquer parte da URL e usar o CDN mais estável
              const driveIdMatch = originalUrl.match(/(?:id=|\/d\/)([-\w]{25,40})/) || originalUrl.match(/[-\w]{25,40}/);
              if (driveIdMatch) {
                const id = driveIdMatch[1] || driveIdMatch[0];
                const cleanId = id.replace('Drive_', '');
                if (cleanId.length >= 25) {
                  console.warn(`🔄 Auto-Cura Capa (LH3): Tentando CDN do Google para ${id}`);
                  target.src = `https://lh3.googleusercontent.com/d/${cleanId}=w1200`;
                  target.dataset.fallbackTried = "true";
                  return;
                }
              }

              // 3. TENTATIVA DE AUTO-CURA (PRIMEIRA MÍDIA)
              // Se tivermos mídia, tentamos usar a primeira como fallback se a capa falhar total
              if (album.media && album.media.length > 0) {
                 const firstMediaUrl = album.media[0].file_url;
                 if (target.src !== firstMediaUrl) {
                    target.src = firstMediaUrl;
                    target.dataset.fallbackTried = "true";
                    return;
                 }
              }
            }}
          />
          {/* Gradients */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-transparent to-transparent" />
          
          {/* Theme Accent: São João Overlay */}
          <div className="absolute inset-0 bg-[#FF6A00]/5 mix-blend-overlay pointer-events-none" />
          
          {/* Fire Glow Effect */}
          <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-full h-[50vh] bg-[#FF6A00]/20 blur-[120px] rounded-full opacity-50" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full px-6 md:px-12 pb-20 md:pb-24 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center gap-4 mb-8">
              <button 
                onClick={() => navigate(-1)}
                className="w-12 h-12 bg-white/5 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-all z-20 group shadow-2xl"
              >
                <ChevronLeft className="w-6 h-6 text-white group-hover:-translate-x-1 transition-transform" />
              </button>

              <button 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: album.name,
                      url: window.location.href
                    }).catch(() => {
                      navigator.clipboard.writeText(window.location.href);
                      toast.success("Link do álbum copiado!");
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Link do álbum copiado!");
                  }
                }}
                className="w-12 h-12 bg-white/5 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-all z-20 group shadow-2xl"
              >
                <Share2 className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              </button>
            </div>
            {/* Tag / Type */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary/20 border border-primary/30 rounded text-[10px] font-black text-primary uppercase tracking-wider">
                <Flame className="w-3 h-3 fill-current" /> SÃO JOÃO VIP
              </div>
              <span className="text-xs font-bold text-white/60 tracking-tight">{album.album_type}</span>
            </div>

            <h1 className="text-5xl md:text-8xl font-display font-black text-white leading-[0.9] mb-4 tracking-normal ">
              {cleanAlbumDescription(album.name) || "GALERIA EXCLUSIVA"}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm md:text-base font-medium text-white/80 mb-6">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                {safeFormatDate(album.created_at)}
              </span>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <span>{album.event_name}</span>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <span className="text-primary font-black uppercase tracking-widest md:text-xs text-[10px] border border-primary/20 px-2 py-0.5 rounded">Exclusivo</span>
            </div>

            {/* Pricing Section */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <div className="bg-white/5 border border-white/10 px-4 py-2 rounded-xl flex flex-col">
                <span className="text-[10px] font-black text-muted-foreground uppercase opacity-60  tracking-widest">R$ Padrão</span>
                <span className="font-display font-black text-xl text-white">
                  {formatPrice(Number(album.meta?.photo_price || 8))}
                  <span className="text-xs font-bold text-white/40 ml-1">/foto</span>
                </span>
              </div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-primary/20 border border-primary/30 px-4 py-2 rounded-xl flex flex-col relative overflow-hidden group/crown"
              >
                <div className="absolute top-0 right-0 p-1">
                  <Crown className="w-3 h-3 text-primary animate-pulse" />
                </div>
                <span className="text-[10px] font-black text-primary uppercase  tracking-widest">R$ Assinante</span>
                <span className="font-display font-black text-xl text-primary drop-shadow-[0_0_10px_rgba(255,106,0,0.3)]">
                  {formatPrice(Number(album.meta?.subscriber_photo_price || album.meta?.photo_price || 3))}
                  <span className="text-xs font-bold text-primary/60 ml-1">/foto</span>
                </span>
              </motion.div>

              <div className="bg-white/10 border border-white/20 px-4 py-2 rounded-xl flex flex-col hover:bg-white/20 transition-all border-dashed">
                <span className="text-[10px] font-black text-white/60 uppercase  tracking-widest">Visitante (Sem Cadastro)</span>
                <span className="font-display font-black text-xl text-white">
                  {formatPrice(12)}
                  <span className="text-xs font-bold text-white/40 ml-1">/foto</span>
                </span>
              </div>

              {album.meta?.package_price && (
                <motion.div 
                   whileHover={hasAccess ? { scale: 1.05 } : {}}
                   whileTap={hasAccess ? { scale: 0.95 } : {}}
                   className={cn(
                     "bg-white/5 border border-white/10 px-4 py-2 rounded-xl flex flex-col relative overflow-hidden group/pack",
                     hasAccess ? "cursor-pointer" : "opacity-40 pointer-events-none"
                   )}
                   onClick={() => hasAccess && navigate(`/checkout?media_ids=${JSON.stringify(album.media.map(m => m.id))}&description=Álbum Completo: ${album.name}`)}
                >
                  <div className="absolute inset-0 bg-primary/0 group-hover/pack:bg-primary/5 transition-colors" />
                  <span className="text-[10px] font-black text-muted-foreground uppercase opacity-60  tracking-widest">FLASH PACK (TUDO)</span>
                  <span className="font-display font-black text-xl text-white">
                    {formatPrice(Number(isSubscriber ? (album.meta.subscriber_package_price || album.meta.package_price) : album.meta.package_price))}
                  </span>
                </motion.div>
              )}

              {!isSubscriber && (
                <button 
                  onClick={() => navigate("/subscription")}
                  className="text-[10px] font-black text-primary underline underline-offset-4 hover:text-primary transition-all uppercase tracking-[0.2em] ml-2 animate-pulse"
                >
                  Quero pagar menos
                </button>
              )}
            </div>

            <p className="text-md md:text-lg text-white/70 max-w-2xl mb-8 leading-relaxed line-clamp-3">
              {cleanAlbumDescription(album.description) || "Reviva os melhores momentos deste evento incrível. Fotos em alta resolução disponíveis para download imediato após a compra."}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => {
                   const gallery = document.getElementById('gallery');
                    gallery?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white text-black hover:bg-white/90 h-14 px-8 rounded-xl font-bold flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 shadow-xl"
              >
                <Play className="w-5 h-5 fill-current" /> VER GALERIA
              </Button>
              {(() => {
                const ownsFullAlbum = (album.media.length > 0 && userPurchases.size >= album.media.length) || isAdmin;
                const isFreeZip = !album.meta?.zip_price || Number(album.meta.zip_price) === 0;
                
                if (album.meta?.zip_link && (ownsFullAlbum || isFreeZip)) {
                  return (
                    <Button 
                      className="bg-primary text-black hover:bg-primary/90 h-14 px-8 rounded-xl font-bold flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 shadow-xl"
                      onClick={() => setIsZipDialogOpen(true)}
                    >
                      <Download className="w-5 h-5" /> BAIXAR ÁLBUM COMPLETO
                    </Button>
                  );
                } else if (album.meta?.zip_link && album.meta?.zip_price) {
                  return (
                    <Button 
                      className="bg-[#25D366] text-white hover:bg-[#25D366]/90 h-14 px-8 rounded-xl font-bold flex items-center gap-3 transition-transform hover:scale-105 active:scale-95 shadow-xl"
                      onClick={() => navigate(`/checkout?album_zip_id=${album.id}&description=Álbum Completo (ZIP): ${album.name}`)}
                    >
                      <Download className="w-5 h-5" /> COMPRAR ÁLBUM (ZIP) (R$ {Number(album.meta.zip_price).toFixed(2).replace('.', ',')})
                    </Button>
                  );
                }
                return null;
              })()}
              <Button 
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20 h-14 px-8 rounded-xl font-bold flex items-center gap-3 backdrop-blur-md transition-transform hover:scale-105"
                onClick={() => {
                  navigator.share({
                    title: cleanAlbumDescription(album.name) || "Galeria By Hudson Raone",
                    url: window.location.href
                  }).catch(() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Link copiado!");
                  });
                }}
              >
                <Share2 className="w-5 h-5" /> COMPARTILHAR
              </Button>
            </div>
            
            {viewers.length > 0 ? (
              <div className="mt-8 flex flex-col items-center sm:items-start gap-3 animate-in fade-in zoom-in duration-500">
                <div className="flex -space-x-3">
                  {viewers.map((viewer, i) => (
                    <div 
                      key={i} 
                      className="relative hover:z-10" 
                      style={{ zIndex: viewers.length - i }}
                      title={viewer.full_name}
                    >
                      <img 
                        src={viewer.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${viewer.full_name}`} 
                        alt={viewer.full_name} 
                        className="w-10 h-10 rounded-full border-2 border-background object-cover shadow-[0_0_15px_rgba(255,106,0,0.3)] hover:scale-110 transition-transform" 
                      />
                      {viewer.is_vip && (
                        <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5 shadow-sm">
                          <Crown className="w-3 h-3 text-primary fill-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-white/50 font-bold uppercase tracking-widest text-[10px]">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                  {Math.max(viewers.length, totalViews)} {Math.max(viewers.length, totalViews) === 1 ? "pessoa visualizou" : "pessoas visualizaram"} recentemente
                </div>
              </div>
            ) : (
              <div className="mt-8 flex items-center gap-2 text-[#FF6A00] font-black uppercase tracking-[0.2em] text-[10px] animate-pulse">
                 <div className="w-2 h-2 rounded-full bg-[#FF6A00]" />
                 Fotos exclusivas disponíveis por tempo limitado
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* São João Subtle Bunting */}
      <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none opacity-30 h-12 flex justify-around items-start overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div key={i} className={cn(
            "w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[30px]",
            i % 3 === 0 ? "border-t-primary" : i % 3 === 1 ? "border-t-yellow-500" : "border-t-red-500"
          )} style={{ marginTop: Math.sin(i) * 10 }} />
        ))}
      </div>

      {/* Gallery Section - Unified Grid */}
      <main id="gallery" className="relative z-10 pt-20 pb-40 px-2 md:px-12 bg-gradient-to-b from-[#0a0a0a] to-black min-h-[55vh]">
        <div className={cn("transition-all duration-700", (!hasAccess || userNeedsPhoto) && "blur-xl select-none pointer-events-none opacity-30")}>
        <div className="flex items-end border-b border-white/10 mb-12 px-4 max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-2">
            <button 
              onClick={() => setActiveTab("gallery")}
              className={cn(
                "px-6 py-3 rounded-t-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all border-t border-x",
                activeTab === "gallery" 
                  ? "bg-[#0a0a0a] text-white border-white/10 border-b-[#0a0a0a] -mb-[1px] font-black scale-105" 
                  : "bg-white/5 text-white/40 border-transparent hover:bg-white/10 hover:text-white"
              )}
            >
              Galeria Geral
            </button>
            {(() => {
              const assignedCount = allMedia.filter(m => Boolean((m as any).assigned_to_user_id)).length;
              if (assignedCount === 0) return null;
              return (
                <button 
                  onClick={() => setActiveTab("assigned")}
                  className={cn(
                    "px-6 py-3 rounded-t-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all border-t border-x flex items-center gap-2.5 relative group overflow-hidden",
                    activeTab === "assigned" 
                      ? "bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-black border-amber-300 font-black shadow-[0_0_20px_rgba(245,158,11,0.6)] -mb-[1px] scale-105" 
                      : "bg-amber-950/40 border-amber-500/30 text-amber-300 hover:bg-amber-900/50 hover:text-amber-200 hover:border-amber-500/50 shadow-[0_0_12px_rgba(245,158,11,0.25)] hover:scale-[1.02]"
                  )}
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none" />
                  <div className="relative flex items-center justify-center">
                    <Crown className="w-4 h-4 sm:w-5 sm:h-5 text-amber-400 fill-amber-400/20 animate-bounce drop-shadow-[0_0_6px_rgba(251,191,36,0.9)]" />
                    <span className="absolute -top-1 -right-1 flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                    </span>
                  </div>
                  <span className="font-extrabold tracking-wider drop-shadow-sm">ASSINANTES VIP</span>
                  <span className={cn(
                    "text-[9px] px-2 py-0.5 rounded-full font-black transition-colors shadow-sm",
                    activeTab === "assigned" ? "bg-black text-amber-400 font-black" : "bg-amber-400/20 text-amber-300 border border-amber-400/30"
                  )}>
                    {assignedCount}
                  </span>
                </button>
              );
            })()}
            {isAdmin && (
              <button 
                onClick={() => setActiveTab("purchased")}
                className={cn(
                  "px-6 py-3 rounded-t-xl text-[10px] sm:text-xs font-black uppercase tracking-widest transition-all border-t border-x flex items-center gap-2",
                  activeTab === "purchased" 
                    ? "bg-[#0a0a0a] text-white border-white/10 border-b-[#0a0a0a] -mb-[1px] font-black scale-105" 
                    : "bg-white/5 text-white/40 border-transparent hover:bg-white/10 hover:text-white"
                )}
              >
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Fotos Compradas
              </button>
            )}
          </div>
        </div>

        {/* Folder / Sector Navigation */}
        {activeTab === "gallery" && (() => {
           const sectors = Array.from(new Set(allMedia.map(m => m.category))).filter(Boolean) as string[];
           if (sectors.length === 0) return null;

           if (selectedSector !== "all") {
             return (
               <div className="flex items-center gap-4 mb-8 max-w-5xl mx-auto px-4 animate-in fade-in">
                 <button
                   onClick={() => setSelectedSector("all")}
                   className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all text-white/60 hover:text-white"
                 >
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                   Voltar
                 </button>
                 <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-xl">
                   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>
                   <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">{selectedSector}</span>
                 </div>
               </div>
             );
           }

           return (
             <div className="mb-12 max-w-5xl mx-auto px-4">
               <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 mb-4 flex items-center gap-3">
                 <span className="w-1 h-4 bg-primary rounded-full" />
                 Pastas do Álbum
               </h2>
               <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-4 duration-700">
                 {sectors.map(sector => {
                   const sectorColor = allMedia.find(m => m.category === sector)?.category_color || "#FF6A00";
                   const sectorCount = allMedia.filter(m => m.category === sector).length;
                   return (
                     <button
                       key={sector}
                       onClick={() => setSelectedSector(sector)}
                       className="relative group p-6 rounded-2xl border transition-all text-left flex flex-col items-start gap-3 bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 hover:scale-[1.02]"
                     >
                       <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: `${sectorColor}20` }}>
                         <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={sectorColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"/></svg>
                       </div>
                       <div>
                         <h3 className="text-xs font-bold text-white uppercase tracking-wider line-clamp-1">{sector}</h3>
                         <p className="text-[10px] text-white/40 mt-1">{sectorCount} fotos</p>
                       </div>
                     </button>
                   );
                 })}
               </div>
             </div>
           );
        })()}

        {(activeTab === "gallery" || activeTab === "assigned") && (
          <div className="flex items-center justify-between mb-8 max-w-5xl mx-auto px-4">
             <h2 className="text-[10px] font-black uppercase tracking-[0.3em] text-white/40 flex items-center gap-3">
               <span className="w-1 h-4 bg-primary rounded-full shadow-[0_0_10px_rgba(255,106,0,0.5)]" />
               {activeTab === "assigned" ? (
                 <span className="text-amber-400 font-black flex items-center gap-2 drop-shadow-[0_0_8px_rgba(245,158,11,0.5)]">
                   <Crown className="w-4 h-4 animate-pulse" /> FOTOS ATRIBUÍDAS AOS ASSINANTES VIP ({allMedia.filter(m => Boolean((m as any).assigned_to_user_id)).length})
                 </span>
               ) : (
                 <>{selectedSector === "all" ? "Fotos Principais" : `Fotos da Pasta`} ({allMedia.filter(m => selectedSector === "all" ? !m.category : m.category === selectedSector).length})</>
               )}
             </h2>
          </div>
        )}

        {(activeTab === "gallery" || activeTab === "assigned") ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4">
          {allMedia
            .filter(m => activeTab === "assigned" ? Boolean((m as any).assigned_to_user_id) : (selectedSector === "all" ? !m.category : m.category === selectedSector))
            .map((item, idx) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (idx % 10) * 0.05 }}
              onClick={() => {
                if (album?.allow_enlarge === false && !isAdmin) {
                  toast.error("A visualização ampliada está desativada para este álbum.");
                  return;
                }
                const hasViewerAccess = isAdmin || planId === "estrela" || planId === "lunar";
                // A restrição estrita foi removida para permitir que usuários (incluindo no iPhone) possam abrir a visualização e ver a foto com marca d'água.
                // As proteções de Anti-IA e Marca d'água continuam ativas dentro do Lightbox.
                const currentGalleryList = allMedia.filter(m => activeTab === "assigned" ? Boolean((m as any).assigned_to_user_id) : (selectedSector === "all" ? !m.category : m.category === selectedSector));
                const targetIdx = currentGalleryList.findIndex(m => m.id === item.id);
                setLightboxIndex(targetIdx >= 0 ? targetIdx : idx);
                setLightboxOpen(true);
              }}
              className="group aspect-[3/4] rounded-xl overflow-hidden bg-transparent flex items-center justify-center p-2 md:p-3 cursor-pointer relative border border-white/10 shadow-2xl"
            >
                  <img 
                    src={item.thumbnail_url || item.file_url} 
                    className="w-full h-full object-contain transition-all duration-700 group-hover:scale-105" 
                    alt="" 
                    loading="lazy"
                    onError={(e) => {
                      handleMediaError(e, item, 400);
                      const target = e.target as HTMLImageElement;
                      if (target.dataset.fallbackTried === "true" && !target.dataset.autoRecovered) {
                        target.dataset.autoRecovered = "true";
                        const driveIdMatch = (item.thumbnail_url || item.file_url || "").match(/[-\w]{25,40}/);
                        if (driveIdMatch) {
                          setTimeout(() => {
                            target.src = `https://drive.google.com/thumbnail?id=${driveIdMatch[0]}&sz=w400&cb=${Date.now()}`;
                          }, 2000);
                        }
                      }
                    }}
                  />
                  {/* Photo Watermark Overlay on Thumbnail */}
                  {album?.meta?.watermark !== false && !userPurchases.has(item.id) && item.media_type !== "video" && (
                    <div className="absolute inset-0 pointer-events-none z-10">
                      {customWatermark ? (
                        watermarkMode === 'tile' ? (
                          <div 
                            className={cn("absolute w-[200%] h-[200%] -top-[50%] -left-[50%]", watermarkRotated ? "-rotate-45" : "")}
                            style={{
                              backgroundImage: `url(${customWatermark})`,
                              backgroundSize: '80px 80px',
                              backgroundRepeat: 'repeat',
                              opacity: watermarkOpacity ?? 0.6,
                              transformOrigin: 'center center'
                            }}
                          />
                        ) : (
                          <div 
                            className="w-full h-full flex items-center justify-center"
                            style={{ opacity: watermarkOpacity ?? 0.6 }}
                          >
                            {watermarkMode === 'fit' ? (
                              <div className="relative w-[90%] h-[90%] pointer-events-none select-none">
                                <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
                              </div>
                            ) : (
                              <div className="relative w-16 h-16 pointer-events-none select-none">
                                <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
                              </div>
                            )}
                          </div>
                        )
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center opacity-40">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <span key={i} className="text-white/30 text-[10px] font-black uppercase tracking-widest rotate-[-30deg] my-2 select-none whitespace-nowrap">
                              {album?.meta?.watermark_text || "By Hudson Raone"}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                {item.media_type === "video" && !userPurchases.has(item.id) && !isFreeWedding && (
                   <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/80 backdrop-blur-md rounded-full flex items-center justify-center shadow-2xl">
                         <Play className="w-5 h-5 md:w-6 md:h-6 text-white fill-current translate-x-0.5" />
                      </div>
                   </div>
                )}

              <div className="absolute top-2 left-2 flex flex-col gap-1.5 z-30 pointer-events-none">
                {(item as any).assigned_to_user_id && (
                  <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-black font-black text-[9px] md:text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-[0_4px_20px_rgba(255,180,0,0.7)] flex items-center gap-1.5 border border-yellow-200 animate-in zoom-in duration-300 transform hover:scale-105">
                    <Crown className="w-3.5 h-3.5 fill-black text-black stroke-[2.5]" />
                    <span className="font-extrabold drop-shadow-sm">ASSINANTE</span>
                  </div>
                )}
                {userPurchases.has(item.id) ? (
                  <div className="bg-green-500 text-white font-black text-[9px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1 border border-green-400/30 w-fit">
                    <Check className="w-3 h-3 stroke-[3]" />
                    <span>Comprada</span>
                  </div>
                ) : isFreeWedding ? (
                  <div className="bg-blue-500 text-white font-black text-[9px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg flex items-center gap-1 border border-blue-400/30 w-fit">
                    <Download className="w-3 h-3 stroke-[3]" />
                    <span>Gratuita</span>
                  </div>
                ) : savedMediaIds.has(item.id) ? (
                  <div className="bg-amber-500 p-1.5 rounded-full shadow-lg border border-amber-400/30 w-fit">
                    <Bookmark className="w-3 h-3 text-white fill-current" />
                  </div>
                ) : null}
              </div>

              {/* Action Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity p-3 flex items-end justify-between">
                {userPurchases.has(item.id) || isFreeWedding || isAdmin ? (
                  <Button 
                    size="icon" 
                    variant="default"
                    className={cn(
                      "w-8 h-8 rounded-full transition-all shadow-[0_0_15px_rgba(34,197,94,0.5)]",
                      isFreeWedding && !userPurchases.has(item.id) ? "bg-blue-500 hover:bg-blue-600 shadow-[0_0_15px_rgba(59,130,246,0.5)]" : "bg-green-500 hover:bg-green-600"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (userPurchases.has(item.id)) {
                        const pDate = userPurchaseDates.get(item.id);
                        if (!isSubscriber && pDate && isDownloadExpired(pDate)) {
                          setExpiredDialogState({ isOpen: true, mediaId: item.id });
                          return;
                        }
                      }
                      downloadMedia((item as any).original_file_url || item.file_url, item.filename || `foto_${item.id}.jpg`);
                      toast.success("Download iniciado!");
                    }}
                  >
                    <Download className="w-4 h-4 text-white" />
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    {!album.allow_downloads ? (
                      <Button 
                        size="icon" 
                        variant={cart.has(item.id) ? "default" : "secondary"}
                        className={cn(
                          "w-8 h-8 rounded-full transition-all",
                          cart.has(item.id) && "scale-110 shadow-[0_0_15px_rgba(255,106,0,0.5)]"
                        )}
                        onClick={(e) => toggleCart(item.id, e)}
                      >
                        {cart.has(item.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 text-white" />}
                      </Button>
                    ) : (
                      <Button 
                        size="icon" 
                        variant={cart.has(item.id) ? "default" : "secondary"}
                        className={cn(
                          "w-8 h-8 rounded-full transition-all",
                          cart.has(item.id) && "scale-110 shadow-[0_0_15px_rgba(255,106,0,0.5)]"
                        )}
                        onClick={(e) => toggleCart(item.id, e)}
                      >
                        {cart.has(item.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button 
                      size="icon" 
                      variant={savedMediaIds.has(item.id) ? "default" : "secondary"}
                      className={cn(
                        "w-8 h-8 rounded-full transition-all",
                        savedMediaIds.has(item.id) && "bg-amber-500 text-white scale-110 shadow-[0_0_15px_rgba(245,158,11,0.5)] hover:bg-amber-600"
                      )}
                      onClick={(e) => toggleBookmark(item.id, e)}
                    >
                      <Bookmark className={cn("w-3.5 h-3.5", savedMediaIds.has(item.id) && "fill-current")} />
                    </Button>
                  </div>
                )}
                <div className="flex flex-col items-end">
                   <span className="text-[8px] font-black text-white/60 uppercase tracking-widest">Ver</span>
                   <Eye className="w-3 h-3 text-white" />
                </div>
              </div>
              
              {cart.has(item.id) && (
                <div className="absolute top-2 right-2 bg-primary p-1 rounded-full shadow-lg z-10 scale-110 animate-in zoom-in duration-300">
                  <CheckCircle2 className="w-3 h-3 text-white" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
        ) : (
          <div className="space-y-8">
            {loadingPurchased ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <GlobalLoaderTrigger />
              </div>
            ) : purchasedMedia.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center opacity-30 select-none">
                <CheckCircle2 className="w-16 h-16 mb-4 text-primary" />
                <p className="text-sm font-black uppercase tracking-[0.3em]">Nenhuma foto vendida neste álbum ainda</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-8">
                {purchasedMedia.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="relative group flex flex-col gap-3"
                  >
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden flex items-center justify-center p-2 bg-transparent border border-white/10 relative group/admin">
                      <img 
                        src={item.thumbnail_url || item.file_url} 
                        className="w-full h-full object-contain" 
                        alt="" 
                      />
                      <div className="absolute top-2 right-2 bg-green-500/90 backdrop-blur-md px-2 py-0.5 rounded-full flex items-center gap-1 shadow-lg z-10">
                        <Check className="w-2.5 h-2.5 text-white" />
                        <span className="text-[9px] font-black text-white">{item.buyers.length}x</span>
                      </div>
                      
                      {/* Admin Direct Download Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover/admin:opacity-100 transition-opacity p-3 flex items-end justify-start z-10">
                        <Button 
                          size="icon" 
                          variant="default"
                          className="w-8 h-8 rounded-full transition-all bg-green-500 hover:bg-green-600 shadow-[0_0_15px_rgba(34,197,94,0.5)]"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadMedia((item as any).original_file_url || item.file_url, item.filename || `foto_${item.id}.jpg`);
                            toast.success("Download iniciado pelo Admin!");
                          }}
                          title="Baixar mídia original"
                        >
                          <Download className="w-4 h-4 text-white" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1.5 px-1 py-2 overflow-hidden">
                      <div className="flex -space-x-2">
                        {item.buyers.slice(0, 4).map((buyer, bIdx) => (
                          <TooltipProvider key={buyer.user_id}>
                            <Tooltip>
                              <TooltipTrigger>
                                <Avatar className="w-7 h-7 border-2 border-black ring-1 ring-white/10">
                                  <AvatarImage src={buyer.avatar_url || ""} />
                                  <AvatarFallback className="bg-primary/20 text-[10px] font-black text-primary uppercase">
                                    {buyer.full_name[0]}
                                  </AvatarFallback>
                                </Avatar>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="bg-black/90 border-white/10">
                                <p className="text-[10px] font-bold uppercase tracking-widest">{buyer.full_name}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ))}
                        {item.buyers.length > 4 && (
                          <div className="w-7 h-7 rounded-full bg-white/5 border-2 border-black flex items-center justify-center text-[10px] font-black text-white/40">
                            +{item.buyers.length - 4}
                          </div>
                        )}
                      </div>
                      <p className="text-[9px] font-bold text-white/40 uppercase tracking-tighter truncate">
                        {item.buyers.length === 1 ? '1 Comprador' : `${item.buyers.length} Compradores`}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
        </div>

        {!hasAccess && (
          <div className="absolute inset-0 z-20 flex items-center justify-center p-6 bg-[#0a0a0a]/65 backdrop-blur-[2px]">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="bg-card/85 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl max-w-sm w-full text-center shadow-[0_20px_50px_rgba(0,0,0,0.8)] sticky top-[20vh]"
            >
              <div className="w-14 h-14 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-primary/30 relative">
                 <Shield className="w-7 h-7 text-primary drop-shadow-[0_0_15px_rgba(255,106,0,0.6)]" />
                 <motion.div 
                   animate={{ opacity: [0, 1, 0] }}
                   transition={{ repeat: Infinity, duration: 2 }}
                   className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-4 border-[#121212]"
                 />
              </div>

              <h3 className="text-xl font-display font-black uppercase tracking-tighter mb-3 text-white">
                 {!user ? "Login Necessário 📸" : "Álbum Privado"}
              </h3>

              <p className="text-[11px] text-muted-foreground leading-relaxed mb-6">
                 {!user 
                   ? "Para visualizar as fotos deste álbum, você precisa entrar com sua conta." 
                   : "Este álbum é de acesso restrito. Solicite autorização ao administrador para visualizar as fotos e realizar compras."}
              </p>

              {accessPending ? (
                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-2xl flex flex-col items-center gap-3">
                   <div className="w-8 h-8 rounded-full border-2 border-orange-500/30 flex items-center justify-center">
                      <Check className="w-4 h-4 text-orange-500 animate-pulse" />
                   </div>
                   <div className="text-center">
                      <p className="text-[9px] font-black uppercase text-orange-500 tracking-widest">Solicitação Pendente</p>
                      <p className="text-[8px] text-muted-foreground mt-1 leading-relaxed">Sua solicitação foi enviada e está sob análise.<br/>Você receberá uma notificação quando for aprovado.</p>
                   </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                   {!user ? (
                     <Button 
                       onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                       className="w-full h-12 bg-gradient-primary text-white font-black uppercase text-xs tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all rounded-xl"
                     >
                       Fazer Login / Cadastrar 📸
                     </Button>
                   ) : (
                     <Button 
                       onClick={handleRequestAccess}
                       disabled={requestLoading}
                       className="w-full h-12 bg-gradient-primary text-white font-black uppercase text-xs tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all rounded-xl"
                     >
                       {requestLoading ? "Processando..." : "Solicitar Acesso 📸"}
                     </Button>
                   )}
                   
                   <Button 
                     variant="ghost" 
                     onClick={() => navigate("/albums")}
                     className="w-full h-10 text-muted-foreground hover:text-white font-bold uppercase text-[9px] tracking-widest"
                   >
                     Voltar para Galerias
                   </Button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </main>

      {/* Floating Checkout Bar */}
      <AnimatePresence>
        {cart.size > 0 && !lightboxOpen && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-6 left-0 right-0 z-[110] flex justify-center px-4"
          >
            <button 
              onClick={() => Boolean(album?.meta?.selection_mode) ? handleSubmitSelection() : navigate(`/checkout?media_ids=${JSON.stringify(Array.from(cart))}`)}
              className="relative p-[3px] w-full max-w-sm rounded-2xl overflow-hidden group shadow-[0_15px_40px_rgba(245,158,11,0.4)] transition-all active:scale-[0.98] outline-none"
            >
              {/* Rotating Glowing Border Background */}
              <div className="absolute inset-[-100%] bg-[conic-gradient(from_0deg,transparent_0_260deg,#fff_320deg,#fff_360deg)] animate-[spin_2s_linear_infinite] opacity-90" />
              
              {/* Inner solid button */}
              <div className="relative w-full h-14 bg-amber-500 rounded-xl flex flex-col items-center justify-center gap-0 overflow-hidden border-b-4 border-black/20 group-active:border-b-0 group-active:translate-y-1 transition-all">
                <div className="flex items-center gap-1.5 text-[10px] text-white/90 tracking-widest font-black leading-none pt-1.5">
                  <span className="whitespace-nowrap">{cart.size} {cart.size === 1 ? 'Item' : 'Itens'}</span>
                  <span>•</span>
                  <span className="whitespace-nowrap">{formatPrice(calculateTotal())}</span>
                </div>
                <div className="flex items-center gap-2 text-[14px] font-black text-white leading-none pb-1.5 uppercase tracking-wide">
                  FINALIZAR SELEÇÃO
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={(allMedia.filter(m => activeTab === "assigned" ? Boolean((m as any).assigned_to_user_id) : (selectedSector === "all" ? !m.category : m.category === selectedSector))).map(m => ({ 
          id: m.id, 
          url: m.file_url || m.thumbnail_url || (m as any).original_file_url, 
          original_url: (m as any).original_file_url || m.file_url, 
          thumbnail_url: m.thumbnail_url,
          type: m.media_type, 
          assigned_to_user_id: (m as any).assigned_to_user_id 
        }))}
        initialIndex={lightboxIndex}
        cartItems={cart}
        onToggleCart={(id) => toggleCart(id)}
        cartCount={cart.size}
        cartTotal={calculateTotal()}
        watermarkUrl={customWatermark}
        watermarkMode={watermarkMode}
          previewQuality={previewQuality}
          vipHighQuality={vipHighQuality}
        watermarkOpacity={watermarkOpacity}
        watermarkRotated={watermarkRotated}
        antiAiProtection={!!(album as any)?.anti_ai_protection}
        antiAiMode={album?.meta?.anti_ai_mode || "grid"}
        getMediaPrice={(id) => {
          const m = album.media.find(item => item.id === id);
          if (m) {
            const isAssignedToMe = Boolean((m as any).assigned_to_user_id && profile && ((m as any).assigned_to_user_id === profile.id || (isSubscriber && (m as any).assigned_to_user_id === "ALL_VIP")));
            let price: number = getPrice(m.media_type, m.media_subtype, isAssignedToMe);
            if (album.meta && user) {
               if (m.media_type === "photo") {
                  if (!isSubscriber && album.meta.photo_price) {
                    price = Number(album.meta.photo_price);
                  }
               } else if (m.media_type === "video") {
                  if (!isSubscriber && album.meta.video_price) {
                    price = Number(album.meta.video_price);
                  }
               }
            }

            if (profile?.is_noiva && m.media_type === "photo" && album.created_at) {
                const createdDate = new Date(album.created_at);
                const diffDays = Math.ceil(Math.abs(new Date().getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
                if (diffDays > 90) {
                    price = 1.00;
                }
            }

            return price;
          }
          return getPrice("photo");
        }}
        onCheckout={() => navigate(`/checkout?media_ids=${JSON.stringify(Array.from(cart))}`)}
        hideWatermark={album?.meta?.watermark === false}
        watermarkText={album?.meta?.watermark_text}
        userPurchases={userPurchases}
        savedMediaIds={savedMediaIds}
        userSelections={userSelections}
        userPurchaseDates={userPurchaseDates}
        isFreeWedding={isFreeWedding}
        onToggleBookmark={toggleBookmark}
      />
      
      <ZipDownloadDialog 
        open={isZipDialogOpen} 
        onOpenChange={setIsZipDialogOpen} 
        zipLink={album?.meta?.zip_link || ""} 
      />

      <ExpiredDownloadDialog 
        isOpen={expiredDialogState.isOpen} 
        onClose={() => setExpiredDialogState({isOpen: false, mediaId: ""})} 
        mediaId={expiredDialogState.mediaId} 
      />

      {/* Modal de Aviso de Preço Exclusivo para Assinante */}
      <AlertDialog open={unassignedPhotoModal.open} onOpenChange={(open) => setUnassignedPhotoModal({ open, mediaId: open ? unassignedPhotoModal.mediaId : null })}>
        <AlertDialogContent className="bg-gradient-to-b from-zinc-900 via-neutral-900 to-black border-2 border-amber-500/60 shadow-[0_0_60px_rgba(245,158,11,0.35)] rounded-3xl p-6 text-white z-[150] max-w-md">
          <AlertDialogHeader className="text-center items-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center shadow-xl mx-auto">
              <Crown className="w-8 h-8 text-amber-400 fill-amber-400 animate-pulse" />
            </div>
            <AlertDialogTitle className="text-lg font-black uppercase tracking-wider text-amber-400">
              Desconto VIP Exclusivo
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-zinc-300 font-medium leading-relaxed text-center space-y-2">
              <p>
                Os valores promocionais de Assinante VIP são exclusivos para as fotos que foram pré-selecionadas e atribuídas para você!
              </p>
              <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-left text-[11px] space-y-1">
                <span className="font-bold text-amber-300 flex items-center gap-1">👥 Foto de colega ou familiar?</span>
                <p>Você pode adicionar um <strong>Convidado</strong> na sua assinatura diretamente no seu Perfil para que ele pague apenas <strong>R$ 8,00</strong> por foto!</p>
              </div>
              <p className="text-zinc-400 text-[11px] pt-1">
                Deseja adicionar esta foto à sua seleção pelo valor avulso regular (R$ 12,00)?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <AlertDialogCancel asChild>
              <button
                type="button"
                onClick={() => setUnassignedPhotoModal({ open: false, mediaId: null })}
                className="w-full sm:w-1/3 h-11 text-xs font-bold bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl transition"
              >
                Cancelar
              </button>
            </AlertDialogCancel>
            <button
              type="button"
              onClick={() => {
                setUnassignedPhotoModal({ open: false, mediaId: null });
                navigate("/profile");
              }}
              className="w-full sm:w-1/3 h-11 text-xs font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl shadow transition flex items-center justify-center gap-1"
            >
              Ir pro Perfil
            </button>
            <button
              type="button"
              onClick={() => {
                if (unassignedPhotoModal.mediaId) {
                  const nextCart = new Set(cart);
                  nextCart.add(unassignedPhotoModal.mediaId);
                  setCart(nextCart);
                }
                setUnassignedPhotoModal({ open: false, mediaId: null });
                toast.success("Foto adicionada pelo valor avulso (R$ 12,00).");
              }}
              className="w-full sm:w-1/3 h-11 text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-black rounded-xl shadow-[0_0_15px_rgba(245,158,11,0.3)] transition"
            >
              Adicionar
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SharedAlbum;
